/** Event page cards: details (date, time, place, audience, description) and my participation. (The songs: practice/ProgramCard.) */
import { Ban, CalendarDays, Clock, MapPin, Repeat, StickyNote, Users, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { EventDTO } from '../../../shared/dto/events';
import { useRequiredMe } from '../../auth/AuthProvider';
import { Button, Card, cx, TextArea } from '../../components/ui';
import { useI18n, useT } from '../../i18n';
import { capitalize, formatDay, formatTimeRange, relativeDayLabel, todayStr } from '../../lib/dates';
import { CheckinControl, RsvpButtons, RsvpChip, useParticipation } from './participation';
import { audienceText, canParticipate, KindBadge, kindKey, Linkified, mapsUrl, OK_FILL } from './shared';

export const NOTE_MAX = 300;

export function CancelledBanner({ ev }: { ev: EventDTO }) {
  const t = useT();
  if (ev.status !== 'cancelled') return null;
  return (
    <div role="status" className="flex items-start gap-3 rounded-2xl bg-[var(--danger)] px-5 py-4 text-white">
      <Ban className="mt-0.5 size-6 shrink-0" aria-hidden />
      <div>
        <p className="text-[18px] font-bold">{t('calendar.event.cancelled_banner')}</p>
        {ev.cancelReason ? <p className="mt-0.5">{t('calendar.event.reason', { reason: ev.cancelReason })}</p> : null}
      </div>
    </div>
  );
}

function Row({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="flex min-h-11 items-center gap-3">
      <Icon className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function EventDetailsCard({ ev, now }: { ev: EventDTO; now: number }) {
  const t = useT();
  const { loc } = useI18n();
  const day = todayStr(ev.startsAt);
  const rel = relativeDayLabel(day, todayStr(now), loc);
  const cancelled = ev.status === 'cancelled';
  const ongoing = !cancelled && ev.startsAt <= now && now < ev.endsAt;
  const ended = ev.endsAt <= now;

  return (
    <Card as="section" className="flex flex-col gap-4 p-5 md:p-6">
      <div className="flex items-start gap-4">
        <KindBadge kind={ev.kind} cancelled={cancelled} size="lg" />
        <div className="min-w-0 flex-1">
          {ev.title !== t(kindKey(ev.kind)) ? <p className="text-[15px] font-semibold text-[var(--muted)]">{t(kindKey(ev.kind))}</p> : null}
          <h2 className={cx('text-[26px] leading-tight font-bold break-words', cancelled && 'text-[var(--muted)] line-through')}>{ev.title}</h2>
          {ongoing ? (
            <span className={cx('mt-1 inline-block rounded-full px-3 py-0.5 text-[13px] font-bold text-white', OK_FILL)}>{t('calendar.event.ongoing')}</span>
          ) : ended && !cancelled ? (
            <span className="mt-1 inline-block rounded-full bg-[var(--surface-2)] px-3 py-0.5 text-[13px] font-bold text-[var(--muted)]">
              {t('calendar.event.ended')}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-1 text-[17px]">
        <Row icon={CalendarDays}>
          <span className="font-semibold">{capitalize(formatDay(ev.startsAt, loc, day.slice(0, 4) !== todayStr(now).slice(0, 4)), loc)}</span>
          {rel ? <span className="text-[var(--muted)]"> · {rel}</span> : null}
        </Row>
        <Row icon={Clock}>
          <span className="tabular-nums">{formatTimeRange(ev.startsAt, ev.endsAt, loc)}</span>
        </Row>
        {ev.location ? (
          <a href={mapsUrl(ev.location)} target="_blank" rel="noopener noreferrer" aria-label={`${ev.location} – ${t('calendar.event.open_map')}`}>
            <Row icon={MapPin}>
              <span className="text-[var(--accent)] underline underline-offset-4">{ev.location}</span>
            </Row>
          </a>
        ) : null}
        <Row icon={Users}>
          <span>
            <span className="text-[var(--muted)]">{t('calendar.event.audience')}: </span>
            {audienceText(ev.audienceMask, t)}
          </span>
        </Row>
        {ev.seriesId ? (
          <Row icon={Repeat}>
            <span className="text-[var(--muted)]">{t('calendar.event.recurring')}</span>
          </Row>
        ) : null}
      </div>

      {ev.description ? (
        <div className="border-t border-[var(--line)] pt-4">
          <h3 className="mb-1 text-[15px] font-semibold text-[var(--muted)]">{t('calendar.event.description')}</h3>
          <p className="text-[17px] break-words whitespace-pre-wrap">
            <Linkified text={ev.description} />
          </p>
        </div>
      ) : null}

    </Card>
  );
}

export function MyParticipationCard({ ev, now }: { ev: EventDTO; now: number }) {
  const t = useT();
  const me = useRequiredMe();
  const { setRsvp, checkin } = useParticipation(ev);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(ev.my.note ?? '');
  const [saving, setSaving] = useState(false);

  if (!canParticipate(ev, me.section)) {
    return (
      <Card className="p-5 text-[var(--muted)]">
        <p>{t('calendar.rsvp.not_invited')}</p>
      </Card>
    );
  }
  const cancelled = ev.status === 'cancelled';
  // The server accepts RSVP changes until the event starts.
  const started = ev.startsAt <= now;
  const current = ev.my.effectiveRsvp;

  const saveNote = async () => {
    if (!current) return;
    setSaving(true);
    const ok = await setRsvp(current, note.trim() || null);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <Card as="section" className="flex flex-col gap-4 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[20px] font-bold">{started || cancelled ? t('calendar.event.your_reply') : t('calendar.rsvp.question')}</h2>
        {!started && !cancelled && current === null ? <RsvpChip ev={ev} /> : null}
      </div>
      <RsvpButtons ev={ev} onPick={(r) => void setRsvp(r)} disabled={cancelled || started} />
      {started && !cancelled ? <p className="text-[15px] text-[var(--muted)]">{t('calendar.rsvp.closed')}</p> : null}
      {started && ev.my.note ? (
        <div className="flex items-start gap-3 rounded-2xl bg-[var(--surface-2)] px-4 py-3">
          <StickyNote className="mt-0.5 size-5 shrink-0 text-[var(--muted)]" aria-hidden />
          <p className="min-w-0 flex-1 break-words whitespace-pre-wrap">{ev.my.note}</p>
        </div>
      ) : null}

      {!cancelled && !started ? (
        editing ? (
          <div className="flex flex-col gap-2">
            <TextArea
              label={t('calendar.note.label')}
              value={note}
              maxLength={NOTE_MAX}
              rows={2}
              className="min-h-20"
              placeholder={t('calendar.note.placeholder')}
              onChange={(e) => setNote(e.target.value)}
              hint={t('calendar.note.counter', { n: note.length, max: NOTE_MAX })}
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setNote(ev.my.note ?? '');
                  setEditing(false);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button busy={saving} onClick={() => void saveNote()} className="flex-1">
                {t('calendar.note.save')}
              </Button>
            </div>
          </div>
        ) : ev.my.note ? (
          <div className="flex items-start gap-3 rounded-2xl bg-[var(--surface-2)] px-4 py-3">
            <StickyNote className="mt-0.5 size-5 shrink-0 text-[var(--muted)]" aria-hidden />
            <p className="min-w-0 flex-1 break-words whitespace-pre-wrap">{ev.my.note}</p>
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11"
              onClick={() => {
                setNote(ev.my.note ?? '');
                setEditing(true);
              }}
            >
              {t('common.edit')}
            </Button>
          </div>
        ) : (
          <div>
            <Button
              variant="ghost"
              icon={StickyNote}
              disabled={!current}
              onClick={() => {
                setNote('');
                setEditing(true);
              }}
              className="-ml-2"
            >
              {t('calendar.note.add')}
            </Button>
            {!current ? <p className="text-[14px] text-[var(--muted)]">{t('calendar.note.need_rsvp')}</p> : null}
          </div>
        )
      ) : null}

      <CheckinControl ev={ev} now={now} onCheckin={() => void checkin()} />
    </Card>
  );
}
